import { get, post, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'AdminAPI';
const BASE_PATH = '/api/v1/admin/magic-words';

const isLocalDev = process.env.REACT_APP_LOCAL_DEV || false;

// Helper function to get auth headers
async function getAuthHeaders() {
  // Skip authentication in local development mode
  if (isLocalDev) {
    return {};
  }

  const session = await fetchAuthSession();
  return {
    Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
  };
}

// List all magic words with pagination
export async function listMagicWords(limit = 20, nextToken = null) {
  try {
    let path = `${BASE_PATH}?limit=${limit}`;

    if (nextToken) {
      path += `&nextToken=${encodeURIComponent(nextToken)}`;
    }

    const headers = await getAuthHeaders();

    const restOperation = get({
      apiName: API_NAME,
      path: path,
      options: { headers }
    });

    const response = await restOperation.response;
    const data = await response.body.json();

    if (data && Array.isArray(data.magicWords)) {
      return {
        magicWords: data.magicWords,
        nextToken: data.nextToken || null
      };
    } else {
      throw new Error('Unexpected data format in API response');
    }
  } catch (error) {
    console.error('Error fetching magic words:', error);
    throw error;
  }
}

// Get a specific magic word
export async function getMagicWord(magicWordId) {
  try {
    const headers = await getAuthHeaders();

    const restOperation = get({
      apiName: API_NAME,
      path: `${BASE_PATH}/${magicWordId}`,
      options: { headers }
    });

    const response = await restOperation.response;

    if (response.statusCode === 404) {
      throw new Error('Magic word not found');
    }

    const data = await response.body.json();
    return data;
  } catch (error) {
    console.error('Error fetching magic word:', error);
    throw error;
  }
}

// Create a new magic word
export async function createMagicWord(description = '') {
  try {
    const headers = await getAuthHeaders();

    const restOperation = post({
      apiName: API_NAME,
      path: BASE_PATH,
      options: {
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: { description }
      }
    });

    const response = await restOperation.response;
    const data = await response.body.json();

    if (response.statusCode === 201) {
      return data;
    } else {
      throw new Error('Failed to create magic word');
    }
  } catch (error) {
    console.error('Error creating magic word:', error);
    throw error;
  }
}

// Update a magic word
export async function updateMagicWord(magicWordId, updates) {
  try {
    const headers = await getAuthHeaders();

    const restOperation = put({
      apiName: API_NAME,
      path: `${BASE_PATH}/${magicWordId}`,
      options: {
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: updates
      }
    });

    const response = await restOperation.response;

    if (response.statusCode === 404) {
      throw new Error('Magic word not found');
    }

    const data = await response.body.json();
    return data;
  } catch (error) {
    console.error('Error updating magic word:', error);
    throw error;
  }
}

// Delete a magic word
export async function deleteMagicWord(magicWordId) {
  try {
    const headers = await getAuthHeaders();

    const restOperation = del({
      apiName: API_NAME,
      path: `${BASE_PATH}/${magicWordId}`,
      options: { headers }
    });

    const response = await restOperation.response;

    if (response.statusCode === 404) {
      throw new Error('Magic word not found');
    }

    if (response.statusCode !== 204) {
      throw new Error('Failed to delete magic word');
    }

    return true;
  } catch (error) {
    console.error('Error deleting magic word:', error);
    throw error;
  }
}